import React, { useMemo, useRef, useState } from 'react';
import { useUi, useAssetCdn } from '@/context/Ui';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer, CdnMedia, RoundPlayButton } from '@/components';
import { shuffleArray } from '@/utils/array';
import { getRandomWords } from '@/utils/words';
import { Activity, ActivityStep } from '../Activity';
import { ActivityTypeProps } from '../types';
import { ActivityTitleStep } from '../components/ActivityTitleStep';
import { GrabGameRootWords } from '../components/GrabGameRootWords';

const getThirdPersonSingular = (verb: string): string => {
  const esEndings = ['ch', 'sh', 's', 'x', 'z', 'o'];

  if (verb.endsWith('y') && !/[aeiou]y$/.test(verb)) {
    return verb.slice(0, -1) + 'ies';
  }

  for (const ending of esEndings) {
    if (verb.endsWith(ending)) {
      return verb + 'es';
    }
  }

  return verb + 's';
};

interface ReadingFormThirdPersonSingularProps extends ActivityTypeProps {
  index: number;
  total: number;
  word: string;
}

const ReadingFormThirdPersonSingularStep = ({
  activity,
  mode,
  isPreview,
  wrapperClassName,
  index,
  total,
  word,
}: ReadingFormThirdPersonSingularProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert } = useUi();

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  const [playedOnce, setPlayedOnce] = useState(false);

  const [playerReady, setPlayerReady] = useState(false);

  const choices = useMemo(
    () => shuffleArray([word, ...getRandomWords(3, 'verbs', [word])]),
    [word],
  );

  const suffix = useMemo(() => {
    const thips = getThirdPersonSingular(word);

    switch (thips) {
      case `${word}s`:
        return 's';
      case `${word}es`:
        return 'es';
      default:
        return 'ies';
    }
  }, [word]);

  return (
    <ActivityStep
      key={`${activity.id}-${index}-1`}
      stepIdentifier={`${activity.id}-${index}-1`}
      activity={activity}
      mode={mode}
      stepNumber={index + 1}
      totalSteps={total}
      wrapperClassName={wrapperClassName}
      isPreview={isPreview}
      showTitle={true}
      showActivityPagination={false}
    >
      <div className="flex flex-col items-center justify-center gap-6">
        <RoundPlayButton
          role="button"
          ariaLabel="Play sound"
          size="sm"
          color="white"
          onClick={() => setPlayedOnce(true)}
          pressHint={!playedOnce}
          disabled={!playerReady}
        />

        <CdnMedia
          className="block mx-auto"
          symbol={word}
          symbolType={SymbolType.Word}
          objectType={CdnObjectType.Image}
          width={200}
          height={200}
        />

        <GrabGameRootWords word={word} suffix={suffix} wordChoices={choices} />

        <AudioPlayer
          ref={playerRef}
          src={mediaUrl || ''}
          onReadySrc={() => setPlayerReady(true)}
          onLoadTimeoutSrc={() =>
            sendAlert('We could not load some of the audio for this lesson.', {
              type: 'warning',
              title: 'There was a problem',
            })
          }
        />
      </div>
    </ActivityStep>
  );
};

export const ReadingFormThirdPersonSingular = ({
  activity,
  mode,
  wrapperClassName,
  isPreview,
}: ActivityTypeProps) => {
  if (!activity.words) {
    throw new Error('Words are required for this activity');
  }

  const { words } = activity;

  return (
    <Activity
      steps={[
        <ActivityTitleStep
          key={`${activity.id}-title`}
          activity={activity}
          mode={mode}
          wrapperClassName={wrapperClassName}
          isPreview={isPreview}
          index={0}
          total={words.length + 1}
        />,
        ...words.flatMap((word, index) => [
          <ReadingFormThirdPersonSingularStep
            key={`${activity.id}-${index}-1`}
            activity={activity}
            mode={mode}
            wrapperClassName={wrapperClassName}
            isPreview={isPreview}
            index={index + 1}
            total={words.length + 1}
            word={word}
          />,
        ]),
      ]}
    />
  );
};
